import { useState } from 'react';
import { Link } from 'react-router-dom';
import { enviosApi } from '../../api/envios';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  FileText,
  MapPin,
  EyeOff,
  Eye,
  X
} from 'lucide-react';
import { useEnvios, useDeleteEnvio, useOcultarEnvio } from '../../hooks/useEnvios';
import { useDebounce } from '../../hooks/useDebounce';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Pagination } from '../../components/ui/Pagination';
import { formatDate } from '../../lib/utils';

const ITEMS_PER_PAGE = 20;

type EstadoEnvio = 'todos' | 'pendiente' | 'en_proceso' | 'entregado' | 'cancelado';

const ESTADOS: { value: EstadoEnvio; label: string; color: string }[] = [
  { value: 'todos', label: 'Todos', color: 'gray' },
  { value: 'pendiente', label: 'Pendiente', color: 'yellow' },
  { value: 'en_proceso', label: 'En Proceso', color: 'blue' },
  { value: 'entregado', label: 'Entregado', color: 'green' },
  { value: 'cancelado', label: 'Cancelado', color: 'red' },
];

const getEstadoBadge = (estado: string) => {
  const variants: Record<string, { variant: 'success' | 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
    pendiente: { variant: 'warning', label: 'Pendiente' },
    en_proceso: { variant: 'info', label: 'En Proceso' },
    entregado: { variant: 'success', label: 'Entregado' },
    cancelado: { variant: 'danger', label: 'Cancelado' },
  };
  const config = variants[estado] || variants.pendiente;
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

export function EnvioList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoEnvio>('todos');
  const [mostrarOcultos, setMostrarOcultos] = useState(false);
  const [page, setPage] = useState(1);

  const [isRastreoModalOpen, setIsRastreoModalOpen] = useState(false);
  const [trackingCodigo, setTrackingCodigo] = useState('');
  const [trackingEventos, setTrackingEventos] = useState<any[]>([]);
  const [isTracking, setIsTracking] = useState(false);
  const [trackingError, setTrackingError] = useState('');

  const debouncedSearch = useDebounce(searchTerm, 300);

  const params: any = { page, limit: ITEMS_PER_PAGE };
  if (estadoFiltro !== 'todos') params.estado = estadoFiltro;
  if (mostrarOcultos) params.incluirOcultos = 'true';
  if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

  const { data: paginationData, isLoading, isError, refetch } = useEnvios(params);
  const deleteEnvio = useDeleteEnvio();
  const ocultarEnvio = useOcultarEnvio();

  const envios = paginationData?.data || [];
  const totalItems = paginationData?.total || 0;
  const totalPages = paginationData?.totalPages || 1;

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleEstadoChange = (value: EstadoEnvio) => {
    setEstadoFiltro(value);
    setPage(1);
  };

  const handleToggleOcultos = () => {
    setMostrarOcultos(!mostrarOcultos);
    setPage(1);
  };

  const handleDelete = async (id: number, numero: string) => {
    if (!confirm(`¿Estás seguro de eliminar el envío ${numero}?`)) return;
    try {
      await deleteEnvio.mutateAsync(id);
      refetch();
    } catch (error: any) {
      // error ya manejado por react-query
    }
  };

  const handleOcultar = async (id: number, numero: string, oculto: boolean) => {
    if (!confirm(`¿${oculto ? 'Mostrar' : 'Ocultar'} el envío ${numero}?`)) return;
    try {
      await ocultarEnvio.mutateAsync(id);
      refetch();
    } catch (error: any) {
      // error ya manejado por react-query
    }
  };

  const handleLimpiarFiltros = () => {
    setSearchTerm('');
    setEstadoFiltro('todos');
    setMostrarOcultos(false);
    setPage(1);
  };

  const handleRastrear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trackingCodigo.trim()) return;

    setIsTracking(true);
    setTrackingError('');
    setTrackingEventos([]);

    try {
      const data = await enviosApi.rastrear(trackingCodigo);

      if (data.success) {
        setTrackingEventos(data.data.eventos);
      } else {
        setTrackingError(data.error || 'Error al buscar el paquete.');
      }
    } catch (err: any) {
      setTrackingError(err.response?.data?.error || 'Error al conectar con el servidor.');
    } finally {
      setIsTracking(false);
    }
  };

  const closeRastreoModal = () => {
    setIsRastreoModalOpen(false);
    setTrackingCodigo('');
    setTrackingEventos([]);
    setTrackingError('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando envíos...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error al cargar los envíos
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Envíos</h1>
          <p className="text-sm text-gray-500">
            {totalItems} envío(s) registrados
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setIsRastreoModalOpen(true)}
            className="flex items-center gap-2 bg-white text-gray-700 border-gray-300"
          >
            <MapPin size={18} className="text-blue-500" />
            Rastrear Envío
          </Button>

          <Link to="/envios/nuevo">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              <Plus size={18} />
              Nuevo Envío
            </Button>
          </Link>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar por cliente, número de envío o email..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 min-w-[180px]">
          <Filter size={16} className="text-gray-400 flex-shrink-0" />
          <select
            value={estadoFiltro}
            onChange={(e) => handleEstadoChange(e.target.value as EstadoEnvio)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all duration-200"
          >
            {ESTADOS.map((e) => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>
        </div>

        <button
          onClick={handleToggleOcultos}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
            mostrarOcultos
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Eye size={16} />
          {mostrarOcultos ? 'Mostrando ocultos' : 'Ver ocultos'}
        </button>

        <Button
          type="button"
          variant="outline"
          onClick={handleLimpiarFiltros}
          className="text-gray-500"
        >
          <Filter size={16} className="mr-2" />
          Limpiar
        </Button>
      </div>

      {/* Lista de envíos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {envios.map((envio: any) => {
          const productos = typeof envio.productos === 'string'
            ? JSON.parse(envio.productos)
            : envio.productos || [];

          const isOculto = envio.oculto === true;

          return (
            <div key={envio.id} className={`bg-white rounded-xl shadow-sm border p-4 hover:shadow-md transition-shadow ${isOculto ? 'border-gray-300 bg-gray-50 opacity-60' : 'border-gray-100'}`}>
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Package size={16} className="text-blue-500" />
                    <h3 className="font-semibold text-gray-800">{envio.numeroEnvio}</h3>
                    {getEstadoBadge(envio.estado)}
                    {isOculto && (
                      <Badge variant="default" className="bg-gray-500 text-white">
                        <EyeOff size={12} className="mr-1" />
                        Oculto
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {envio.clienteNombre}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Link
                    to={`/envios/${envio.id}`}
                    className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                  >
                    <Eye size={16} />
                  </Link>
                  {envio.estado === 'pendiente' && (
                    <Link
                      to={`/envios/editar/${envio.id}`}
                      className="p-1.5 text-gray-500 hover:bg-gray-50 rounded transition-colors"
                    >
                      <Edit size={16} />
                    </Link>
                  )}
                  {(envio.estado === 'pendiente' || envio.estado === 'entregado' || envio.estado === 'cancelado') && (
                    <button
                      onClick={() => handleDelete(envio.id, envio.numeroEnvio)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Detalles */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Cliente</span>
                  <span className="font-medium text-gray-700 truncate max-w-[150px]">
                    {envio.clienteNombre}
                  </span>
                </div>
                {envio.clienteDocumento && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Documento</span>
                    <span className="text-xs text-gray-600">
                      {envio.clienteDocumento}
                    </span>
                  </div>
                )}
                {envio.clienteEmail && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <a
                      href={`mailto:${envio.clienteEmail}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline transition-colors text-sm truncate max-w-[150px]"
                    >
                      {envio.clienteEmail}
                    </a>
                  </div>
                )}
                {envio.clienteTelefono && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Teléfono</span>
                    <a
                      href={`tel:${envio.clienteTelefono.replace(/\s/g, '')}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline transition-colors text-sm"
                    >
                      {envio.clienteTelefono}
                    </a>
                  </div>
                )}
                {envio.clienteDireccion && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Dirección</span>
                    <span className="text-xs text-gray-600 truncate max-w-[150px]">
                      {envio.clienteDireccion}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Productos</span>
                  <span className="font-medium text-gray-700">
                    {productos.length} producto(s)
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Fecha</span>
                  <span className="text-xs text-gray-400">
                    {formatDate(envio.fechaCreacion || envio.createdAt)}
                  </span>
                </div>
                {envio.numeroSeguimiento && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Seguimiento</span>
                    <span className="text-xs text-gray-400 font-mono bg-gray-100 px-1 rounded">
                      {envio.numeroSeguimiento}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Email confirmación</span>
                  <span className={`font-medium text-xs ${envio.emailEnviado ? 'text-green-600' : 'text-yellow-600'}`}>
                    {envio.emailEnviado ? ' Enviado' : ' Pendiente'}
                  </span>
                </div>
              </div>

              {/* Acciones */}
              <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2 flex-wrap">
                {envio.estado === 'pendiente' && (
                  <Link
                    to={`/envios/${envio.id}`}
                    className="flex-1 flex items-center justify-center gap-1 text-sm text-gray-700 border border-gray-300 hover:bg-gray-50 py-1.5 rounded-lg transition-colors"
                  >
                    <Truck size={14} />
                    Gestionar
                  </Link>
                )}
                {envio.estado === 'entregado' && (
                  <span className="flex-1 flex items-center justify-center gap-1 text-sm text-green-600 border border-green-200 bg-green-50 py-1.5 rounded-lg">
                    <CheckCircle size={14} />
                    Entregado
                  </span>
                )}
                {envio.estado === 'en_proceso' && (
                  <span className="flex-1 flex items-center justify-center gap-1 text-sm text-blue-600 border border-blue-200 bg-blue-50 py-1.5 rounded-lg">
                    <Truck size={14} />
                    En Camino
                  </span>
                )}
                {envio.estado === 'cancelado' && (
                  <span className="flex-1 flex items-center justify-center gap-1 text-sm text-red-600 border border-red-200 bg-red-50 py-1.5 rounded-lg">
                    <XCircle size={14} />
                    Cancelado
                  </span>
                )}

                <button
                  onClick={() => handleOcultar(envio.id, envio.numeroEnvio, envio.oculto)}
                  className={`flex items-center justify-center gap-1 text-sm border py-1.5 px-3 rounded-lg transition-colors ${
                    envio.oculto
                      ? 'text-blue-600 border-blue-300 hover:bg-blue-50'
                      : 'text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {envio.oculto ? <Eye size={14} /> : <EyeOff size={14} />}
                  {envio.oculto ? 'Mostrar' : 'Ocultar'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {envios.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <FileText size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No se encontraron envíos</p>
          {searchTerm && (
            <p className="text-sm mt-1">
              No hay envíos que coincidan con "{searchTerm}"
            </p>
          )}
          {mostrarOcultos && (
            <p className="text-sm mt-1">
              No hay envíos ocultos para mostrar
            </p>
          )}
          <Link to="/envios/nuevo" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
            Crear el primer envío
          </Link>
        </div>
      )}

      {totalItems > 0 && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* Modal de Rastreo */}
      {isRastreoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">

            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Truck className="text-blue-600" size={20} />
                Seguimiento Correo Argentino
              </h2>
              <button
                onClick={closeRastreoModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              <form onSubmit={handleRastrear} className="flex gap-2 mb-6">
                <input
                  type="text"
                  value={trackingCodigo}
                  onChange={(e) => setTrackingCodigo(e.target.value.toUpperCase())}
                  placeholder="Ej: 180176G2204I471801"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none uppercase font-mono"
                />
                <Button
                  type="submit"
                  disabled={isTracking || !trackingCodigo.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isTracking ? 'Buscando...' : 'Rastrear'}
                </Button>
              </form>

              {trackingError && (
                <div className="p-3 mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <XCircle size={16} className="mt-0.5 flex-shrink-0" />
                  <p>{trackingError}</p>
                </div>
              )}

              {trackingEventos.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm text-gray-500 uppercase tracking-wider mb-4">
                    Historial de Movimientos
                  </h3>
                  <div className="relative border-l-2 border-blue-200 ml-3 pl-5 space-y-5">
                    {trackingEventos.map((evento, index) => (
                      <div key={index} className="relative">
                        <div className={`absolute -left-[27px] h-3.5 w-3.5 rounded-full border-2 border-white ${index === 0 ? 'bg-blue-500' : 'bg-gray-300'}`}></div>

                        <div className="bg-gray-50 border border-gray-100 p-3 rounded-lg shadow-sm">
                          <p className={`font-bold text-sm ${index === 0 ? 'text-blue-700' : 'text-gray-700'}`}>
                            {evento.status}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Clock size={12} /> {evento.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin size={12} /> {evento.facility}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}